import { ApiClient } from './client';
import {
	Client,
	ClientFormData,
	ClientsResponse,
	DashboardData,
	FrontendPaginationMeta,
	LoginResponse,
	PaginationMeta,
	SetupInstructions,
	User,
} from '../../types';

class AdminAPIService extends ApiClient {
	constructor() {
		super('/api/admin');
	}

	// Authentication
	async login(email: string, password: string): Promise<{ user: User; success: boolean }> {
		const response = await fetch('/api/auth/login', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ email, password }),
		});

		if (!response.ok) {
			const error = await response.json().catch(() => ({ message: 'Login failed' }));
			throw new Error(error.message || 'Login failed');
		}

		const data: LoginResponse = await response.json();

		if (data.success && data.tokens?.accessToken) {
			this.setToken(data.tokens.accessToken);

			// Store refresh token if provided
			if (data.tokens.refreshToken) {
				this.setRefreshToken(data.tokens.refreshToken);
			}

			this.setUser(data.user);
			return { user: data.user, success: true };
		}

		throw new Error('Invalid login response');
	}

	logout(): void {
		this.clearAuth();
		window.location.href = '/login';
	}

	getCurrentUser(): User | null {
		return this.getUser();
	}

	isAuthenticated(): boolean {
		return this.hasValidToken();
	}

	// Dashboard APIs
	async getDashboard(): Promise<DashboardData> {
		const response = await this.request('/dashboard');
		return response.data;
	}

	// Sync Activity APIs
	async getSyncActivities(
		page = 1,
		limit = 10,
		clientId?: string,
		filters?: {
			startDate?: string;
			endDate?: string;
			entityType?: string;
			status?: string;
			entityId?: string;
			errorText?: string;
		},
	): Promise<{
		activities: any[];
		pagination: FrontendPaginationMeta;
	}> {
		const params: Record<string, string> = {
			page: page.toString(),
			limit: limit.toString(),
		};
		// Only add clientId if explicitly provided (for ADMIN filtering)
		if (clientId) {
			params.clientId = clientId;
		}
		// Add filter parameters if provided
		if (filters) {
			if (filters.startDate) params.startDate = filters.startDate;
			if (filters.endDate) params.endDate = filters.endDate;
			if (filters.entityType) params.entityType = filters.entityType;
			if (filters.status) params.status = filters.status;
			if (filters.entityId) params.entityId = filters.entityId;
			if (filters.errorText) params.errorText = filters.errorText;
		}

		const queryParams = new URLSearchParams(params);
		const response = await this.request(`/sync/activity?${queryParams}`);

		// Transform backend response to match frontend expectations
		return {
			activities: response.data || [],
			pagination: {
				currentPage: response.pagination.page,
				totalPages: response.pagination.totalPages,
				totalItems: response.pagination.total,
				itemsPerPage: response.pagination.limit,
			},
		};
	}

	// Client Management APIs
	async getClients(params?: {
		page?: number;
		limit?: number;
		search?: string;
		status?: string;
		healthStatus?: string;
	}): Promise<{
		clients: Client[];
		pagination: PaginationMeta;
		summary: any;
	}> {
		const queryString = new URLSearchParams(
			Object.entries(params || {})
				.filter(([_, value]) => value !== undefined && value !== '')
				.map(([key, value]) => [key, String(value)]),
		).toString();

		const response: ClientsResponse = await this.request(`/clients${queryString ? `?${queryString}` : ''}`);

		return {
			clients: response.data.map(this.transformClientData),
			pagination: response.pagination,
			summary: response.summary,
		};
	}

	async getClientDetails(clientId: string): Promise<Client> {
		const response = await this.request(`/clients/${clientId}`);
		// Backend returns client data nested under 'client' property
		return this.transformClientData(response.data.client);
	}

	async createClient(clientData: ClientFormData): Promise<any> {
		const response = await this.request('/clients', {
			method: 'POST',
			body: JSON.stringify(clientData),
		});
		return response.data;
	}

	async updateClient(clientId: string, updates: any): Promise<any> {
		const response = await this.request(`/clients/${clientId}`, {
			method: 'PATCH',
			body: JSON.stringify(updates),
		});
		return response.data;
	}

	async deactivateClient(clientId: string): Promise<any> {
		const response = await this.request(`/clients/${clientId}`, {
			method: 'DELETE',
		});
		return response.data;
	}

	// Sync Management APIs
	async retrySyncLog(syncLogId: string): Promise<any> {
		const response = await this.request(`/sync/retry/${syncLogId}`, {
			method: 'POST',
		});
		return response.data;
	}

	async markAsManuallyTriggered(syncLogId: string): Promise<any> {
		const response = await this.request(`/sync/mark-manually-triggered/${syncLogId}`, { method: 'POST' });
		return response.data;
	}

	async bulkRetrySyncLogs(payload: {
		syncLogIds?: string[];
		filters?: {
			status?: string;
			entityType?: string;
			entityId?: string;
			errorText?: string;
			clientId?: string;
			startDate?: string;
			endDate?: string;
		};
	}): Promise<any> {
		const response = await this.request('/sync/bulk-retry', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
		return response.data;
	}

	// Setup Instructions
	async getSetupInstructions(clientId: string): Promise<{
		success: boolean;
		data: SetupInstructions;
	}> {
		try {
			const response = await this.request(`/clients/${clientId}/setup-instructions`);
			return {
				success: true,
				data: response.data,
			};
		} catch (error) {
			console.error('Failed to get setup instructions:', error);
			throw error;
		}
	}

	/**
	 * Normalises raw backend client data into the frontend Client type.
	 * Backend sends: clientId, clientSecret, webhookSecret (no gp prefix)
	 * Frontend uses: gpClientId, gpClientSecret, gpWebhookSecret (gp prefix)
	 */
	private transformClientData(client: any): Client {
		return {
			id: client.id,
			name: client.name,
			email: client.email,
			status: client.status,
			health: client.health || {
				status: 'healthy' as const,
				issues: [],
			},
			integrations: client.integrations || {
				zoho: { enabled: false, configured: false },
				odoo: { enabled: false, configured: false },
				msd: { enabled: false, configured: false },
			},
			syncStats: client.syncStats || {
				last24h: 0,
				successRate: 0,
				failedSyncs: 0,
			},
			queue: client.queue || {
				waiting: 0,
				failed: 0,
			},
			monthlyVolume: client.syncStats?.last24h || 0,
			createdAt: client.createdAt,
			updatedAt: client.updatedAt,
			type: client.type,
			apiKey: client.apiKey,
			// Normalise to gp-prefixed names regardless of which key the backend sent
			gpClientId: client.clientId || client.gpClientId,
			gpClientSecret: client.clientSecret || client.gpClientSecret,
			gpWebhookSecret: client.webhookSecret || client.gpWebhookSecret,
			config: client.config,
			locations: client.locations || [],
		};
	}

	// User Management APIs
	async listUsers(): Promise<{ success: boolean; data: any[] }> {
		const response = await fetch('/api/auth/users', {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.getToken()}`,
			},
		});

		if (!response.ok) {
			const error = await response.json().catch(() => ({ message: 'Failed to fetch users' }));
			throw new Error(error.message || 'Failed to fetch users');
		}

		return await response.json();
	}

	async createUser(userData: {
		email: string;
		firstName: string;
		lastName: string;
		password: string;
		role: string;
		clientId?: string;
	}): Promise<{ success: boolean; data: any }> {
		const response = await fetch('/api/auth/users', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.getToken()}`,
			},
			body: JSON.stringify(userData),
		});

		if (!response.ok) {
			const error = await response.json().catch(() => ({ message: 'Failed to create user' }));
			throw new Error(error.error || error.message || 'Failed to create user');
		}

		return await response.json();
	}
}

export const adminAPI = new AdminAPIService();
